import { Component, ViewChild } from "@angular/core";
import { Platform, AlertController, Nav, Events, Icon } from "ionic-angular";
import { StatusBar } from "@ionic-native/status-bar";
import { SplashScreen } from "@ionic-native/splash-screen";
import { Storage } from "@ionic/storage";
import { File } from "@ionic-native/file";

import moment from "moment";
import "moment/locale/es";

import {
  LOGIN_PAGE,
  STORAGE,
  EVT_SESSION_EXPIRED,
  EVT_SESSION_CLIENT_CHANGED,
  LOCALE,
  DIR_DOWNLOADS,
  APP_VERSION,
  TOAST_DUR_EXTRA_LONG,
  HORA_CORTE,
} from "../constants";
import { NavMenuProvider } from "../providers/nav-menu/nav-menu";
import { SessionProvider } from "../providers/session/session";
import { MediwareHubService } from "../providers/mediware-hub/mediware-hub";

@Component({
  templateUrl: "app.html",
})
export class MyApp {
  _showedAlert: boolean = false;
  _confirmAlert: any = null;
  private estaActualizado = true;
  private versionNumber = "unknown";
  vigilanteInterval: any;

  rootPage = LOGIN_PAGE;
  @ViewChild(Nav) nav: Nav;

  deferredPrompt: any;
  showInstallButton: boolean = false;

  pages: any[] = [
    { title: "Clientes", component: "ClientsPage", iconName: "swap" },
    { title: "Turnos", component: "AppointmentsPage", iconName: "calendar" },
    { title: "Pacientes", component: "MedicalHistoryPage", iconName: "people" },
    {
      title: "Nomencladores",
      component: "NomencladoresPage",
      iconName: "mediwareapp-reader-outline",
    },
    { title: "Listados", component: "ListadosPage", iconName: "list" },
    {
      title: "Recetas",
      component: "MedicalHistoryPage",
      iconName: "mediwareapp-botonrx-menuprincipal",
      params: { panel: "recetas" },
    },
    {
      title: "Instalar App",
      component: "INSTALL_PWA",
      iconName: "download",
      isPWA: true,
    },
  ];

  footerPages: any[] = [
    // la pagina de settings esta vacia
    { title: "Ajustes", component: "SettingsPage", iconName: "settings" },
    {
      title: "Error log",
      component: "ErrorLogPage",
      iconName: "information-circle",
    },
    {
      title: "Novedades",
      component: "NovedadesPage",
      iconName: "megaphone",
    },
    {
      title: "Acerca de",
      component: "AboutPage",
      iconName: "information-circle",
    },
  ];

  constructor(
    private platform: Platform,
    private statusBar: StatusBar,
    private splashScreen: SplashScreen,
    private navMenu: NavMenuProvider,
    public session: SessionProvider,
    private storage: Storage,
    public events: Events,
    private alertCtrl: AlertController,
    private file: File,
    private mediwareHubService: MediwareHubService,
  ) {
    this.platform.ready().then(() => {
      console.log("👀 App iniciada...");

      // 👇 Escucha mensaje del Service Worker para recargar si hay nueva versión
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.addEventListener("message", (event) => {
          if (event.data && event.data.type === "NUEVA_VERSION") {
            console.log("Nueva versión disponible, recargando...");
            window.location.reload();
          }
        });
      }

      if (this.session.isActive()) {
        this.iniciarVigilante();
      }

      this.platform.resume.subscribe(() => {
        if (this.session.isActive()) {
          this.chequearVencimiento();
        }
      });

      this.events.subscribe("user:login", () => {
        console.log("Usuario se acaba de loguear. Encendiendo vigilante.");
        this.iniciarVigilante();
      });

      this.statusBar.styleBlackTranslucent();
      this.splashScreen.hide();

      moment.locale(LOCALE);

      // Botón atrás
      this.platform.registerBackButtonAction(() => {
        if (this.nav.length() == 1) {
          if (!this._showedAlert) {
            this._confirmExitApp();
          } else {
            this._showedAlert = false;
            this._confirmAlert.dismiss();
          }
        }
        this.nav.pop();
      });

      if (this.platform.is("cordova")) {
        console.log("📱 Entorno nativo - usando File");

        this.file
          .checkDir(this.file.dataDirectory, DIR_DOWNLOADS)
          .then(() => {
            return this.file.removeRecursively(
              this.file.dataDirectory,
              DIR_DOWNLOADS,
            );
          })
          .then(() => {
            return this.file.createDir(
              this.file.dataDirectory,
              DIR_DOWNLOADS,
              true,
            );
          })
          .then(() => {
            console.log("downloads recreated");
          })
          .catch((err) => {
            console.log("Error manejando directorio:", err);
          });
      } else {
        console.log("🌐 Web/PWA - se omite File");
      }
    });

    // Eventos globales
    this.events.subscribe(EVT_SESSION_EXPIRED, () => {
      this._redirectToLogin();
    });

    this.events.subscribe(EVT_SESSION_CLIENT_CHANGED, () => {
      this.pages.find((p) => p.component === "ClientsPage").title =
        `Conectado a ${this.session.getActiveClientName()}`;
    });

    // Página inicial
    this.navMenu.setActivePage(this.pages[1].component);
    this.navMenu.setActivePageTitle(this.pages[1].title);

    // PWA install prompt
    window.addEventListener("beforeinstallprompt", (event: any) => {
      console.log("Evento beforeinstallprompt detectado", event);
      event.preventDefault();
      this.deferredPrompt = event;
      this.showInstallButton = true;   
     });

     window.addEventListener("appinstalled", (event) => {
      console.log("¡La PWA fue instalada exitosamente!");
      this.showInstallButton = false; 
      this.deferredPrompt = null;
    });
  }

 async installPWA() {
  if (!this.deferredPrompt) return;

  this.deferredPrompt.prompt();
  const choiceResult = await this.deferredPrompt.userChoice;
  
  if (choiceResult.outcome === "accepted") {
    console.log("Usuario aceptó instalar");
    this.showInstallButton = false; // Se oculta del menú al instalar
  }
  
  this.deferredPrompt = null;
}

  openPage(page) {
    if (page.isPWA) {
      this.installPWA();
      return;
    }
    if (
      !this.session.getActiveClient() &&
      this.esPaginaRestringida(page.component)
    ) {
      console.log("Acceso denegado: No hay cliente activo");
      return;
    }

    this.nav.setRoot(page.component, page["params"]);
    this.navMenu.setActivePage(page.component);
    this.navMenu.setActivePageTitle(page.title);
  }

  esPaginaRestringida(componente: string): boolean {
    const paginasRestringidas: string[] = [
      "AppointmentsPage",
      "MedicalHistoryPage",
      "CoberturasPage",
      "ListadosPage",
      "NomencladoresPage",
    ];
    return paginasRestringidas.indexOf(componente) !== -1; // Si está en la lista, devuelve true
  }

  pushPage(page) {
    this.nav.push(page.component);
    this.navMenu.setActivePage(page.component);
    this.navMenu.setActivePageTitle(page.title);
  }

  checkActive(page): boolean {
    return this.navMenu.getActivePage() == page.component;
  }
  checkActiveTitle(page): boolean {
    return this.navMenu.getTitleActivePage() == page.title;
  }

  goLogout() {
    let confirm = this.alertCtrl.create({
      title: "Cerrar sesión",
      message: "¿Estás seguro de cerrar sesión?",
      buttons: [
        {
          text: "Cancelar",
          handler: () => {},
        },
        {
          text: "Aceptar",
          handler: () => {
            // logout y redirije a login
            this._doLogOut();

            this.nav.setRoot(LOGIN_PAGE);
          },
        },
      ],
    });
    confirm.present();
  }

  _doLogOut(): void {
    this.esUltimaVersion();
    this.detenerVigilante();

    // borra datos de usuario almacenados
    this.storage.set(STORAGE.REMEMBER_ME, true);
    this.storage.set(STORAGE.PASSWORD, "");
    this.session.removeSession();
  }

  _redirectToLogin(): void {
    let confirm = this.alertCtrl.create({
      title: "Sesión expirada",
      message: "Su sesión ha expirado. Por favor, vuelva a ingresar",
      buttons: [
        {
          text: "Continuar",
          handler: () => {
            // logout y redirije a login
            this._doLogOut();
            this.nav.setRoot(LOGIN_PAGE);
          },
        },
      ],
    });
    confirm.present();
  }

  _confirmExitApp() {
    this._showedAlert = true;
    this._confirmAlert = this.alertCtrl.create({
      title: "Salir",
      message: "¿Está seguro que desea salir de la aplicación?",
      buttons: [
        {
          text: "Cancelar",
          handler: () => {
            this._showedAlert = false;
          },
        },
        {
          text: "Aceptar",
          handler: () => {
            this._doLogOut();

            this.platform.exitApp();
          },
        },
      ],
    });
    this._confirmAlert.present();
  }

  esUltimaVersion(): void {
    console.log("Verificando si la aplicación está actualizada...");
    let versionServidor = "";
    let versionLocal = APP_VERSION.split(".");
    let total = versionLocal.length;
    this.estaActualizado = true; //por defecto
    if (!this.mediwareHubService.getInDevelopment()) {
      return; //para dev
    }

    this.mediwareHubService.obtenerVersion().subscribe(
      (data) => {
        versionServidor = data["appVersion"].split(".");
        if (versionServidor.length <= total) {
          //la cantidad de items, si en servidor tiene mas items es porque hay una sub-version mas nueva y necesita actualizarse
          this.estaActualizado = true;
          for (let i = 0; i < total; i++) {
            if (parseFloat(versionServidor[i]) > parseFloat(versionLocal[i])) {
              //si la del servidor es mas grande que la local
              this.estaActualizado = false; //actualizar!
              break;
            }
          }
        } else {
          this.estaActualizado = false; //actualizar!
        }
        if (!this.estaActualizado) {
          //actualizar a la fuerza
          this.actualizarForzosamente();
        }
      },
      (err) => {
        console.log(err);
      },
    );
  }

  actualizarForzosamente(): void {
    console.log("Forzando actualización de la aplicación");
    if (!this.mediwareHubService.getInDevelopment()) {
      console.log("Modo desarrollo: no se fuerza actualización");
      window.open("https://mediware.com.ar/MediwareApp/", "_parent");
    }
  }

  initializeApp() {
    console.log("Inicializando la aplicación y el vigilante de sesión...");
    this.platform.ready().then(() => {
      // 1. Chequeo al iniciar
      this.chequearVencimiento();

      // 2. Chequeo al volver del segundo plano
      this.platform.resume.subscribe(() => {
        this.chequearVencimiento();
      });

      
      setInterval(() => {
        this.chequearVencimiento();
      }, 60000);
    });
  }

  // 3. LA LÓGICA DEL VIGILANTE
  chequearVencimiento() {
    console.log("Vigilante de sesión: chequeando vencimiento...");

    if (!this.session.isActive()) {
      console.log(
        "No hay sesión activa. No es necesario chequear vencimiento.",
      );
      return; // Si no hay sesión activa, no hace falta chequear nada.
    }
    this.storage.get("SESSION_EXPIRATION").then((expirationTime) => {
      // Si devuelve null, es que el usuario no está logueado, así que no hacemos nada.
      if (!expirationTime) return;

      const ahora = Date.now();

      // COMPARACIÓN: ¿La hora actual es mayor que la hora límite (00:00)?
      if (ahora > expirationTime) {
        console.warn("⚠️ Pasaron las 00:00hs. La sesión ha caducado.");

        // Ejecutamos la salida forzosa
        this.ejecutarAutoLogout();
      }
    });
  }

  // 4. METODO AUXILIAR PARA CERRAR Y ACTUALIZAR
  ejecutarAutoLogout() {
    console.log("Ejecutando cierre de sesión automático...");
    this._doLogOut();
    this.nav.setRoot(LOGIN_PAGE);
  }

  iniciarVigilante() {
    // Si ya existe uno corriendo, no creamos otro para no duplicar
    if (this.vigilanteInterval) {
      return;
    }

    console.log("🟢 Iniciando vigilancia de sesión...");

    // Chequeamos cada 1 minuto (60000 ms) o el tiempo que prefieras
    this.vigilanteInterval = setInterval(() => {
      this.chequearVencimiento();
    }, 60000);
  }

  // Apaga el reloj
  detenerVigilante() {
    console.log("Deteniendo vigilancia de sesión...");
    if (this.vigilanteInterval) {
      console.log("🔴 Deteniendo vigilancia (Logout o Salida).");
      clearInterval(this.vigilanteInterval);
      this.vigilanteInterval = null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts